/* eslint-disable indent */
/* eslint-disable @typescript-eslint/no-unused-vars */
import Box from '@mui/material/Box';
import {uuid} from '../../../../../utils/utils';
import React, {ReactElement, useContext, useState} from 'react';
import Stack from '@mui/material/Stack';
import {useWorkspaceContext} from '../../../../../context/workspaceContext';
import {ResourceType} from '../../../../../@types/resource-type';
import Group from '../../../../../@types/group';
import {fetchAllResources} from '../../../../../utils/resource-utils';
import {OccurrenceReportDescriptorEditorProps} from '../../../../../@types/editor-props';
import ErrorAlertComponent from '../../../../ErrorAlert.component';
import {ResourceAction} from '../../../../../@types/resource-action';
import {UserContext} from '../../../../../context/userContext';
import {toast} from 'react-toastify';
import OccurrenceReportDescriptor from '../../../../../@types/occurrence-report-descriptor';
import occurrenceReportDescriptorService from '../../../../../services/occurrence-report-descriptor.service';
import {useDescriptorForm} from './useDescriptorForm';
import {useTermsManager} from './useTermsManager';
import {isFieldDisabled, useDescriptorPermissions} from './useDescriptorPermissions';
import {DescriptorBasicInfo} from './components/DescriptorBasicInfo.component';
import {DescriptorTermsTable} from './components/DescriptorTermsTable.component';
import {DescriptorActions} from './components/DescriptorActions.component';
import {DescriptorTest} from './components/DescriptorTest.component';
import {DescriptorApply} from './components/DescriptorApply.component';
import {DescriptorState} from './descriptor-constants';

export default function OccurrenceReportDescriptorEditor(props: OccurrenceReportDescriptorEditorProps): ReactElement {
    const { occurrenceReportDescriptor, isActive, updateTab } = props;

    const { user } = useContext(UserContext);
    const [groups, setGroups] = React.useState<Group[]>([]);
    const [error, setError] = useState<string|null>(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
    
    // Use custom hooks for state management
    const {
        formState,
        validation,
        handleNameChange,
        handleDescriptionChange,
        handleUriChange,
        handleUriBlur,
        handleGroupChange,
        validateForm,
        loadDescriptor
    } = useDescriptorForm(occurrenceReportDescriptor);
    
    const {
        termEntries,
        updateTermKey,
        updateTermValue,
        removeTerm,
        handleAddNewTerm,
        canAddNewTerm,
        canRemoveTerm,
        getTermsForSubmission,
        loadTerms
    } = useTermsManager(occurrenceReportDescriptor?.descriptorTerms);
    
    const permissions = useDescriptorPermissions(occurrenceReportDescriptor, user);
    const uuidRef = React.useRef(uuid());
    const { setCurrentWorkspaceObject, workspaceObjects, setWorkspaceObjects, targetGroup,  getAdditionalResource, addOrUpdateAdditionalResource, currentAction, setCurrentAction, publishResourceUpdate } = useWorkspaceContext();


    React.useEffect(() => {
        if (isActive && currentAction === ResourceAction.SAVE) {
            setCurrentAction(null);
            handleSave();
        }
    }, [currentAction])

    React.useEffect(() => {
        let existingGroups = getAdditionalResource(ResourceType.GROUPS).resources;
        if (!existingGroups) {
            fetchAllResources(ResourceType.GROUPS)?.then((grps) => {
                grps.sort((a, b) => a.name.localeCompare(b.name))
                setGroups(grps);
                const defaultGroupId = occurrenceReportDescriptor?.group?.id ?? targetGroup ?? ((grps && grps[0]) ? grps[0].id! : 0);
                handleGroupChange({ target: { value: defaultGroupId } });
                addOrUpdateAdditionalResource({resources: grps, objectType: ResourceType.GROUPS});
            })
        } else {
            existingGroups.sort((a, b) => a.name.localeCompare(b.name))
            setGroups(existingGroups);
            const defaultGroupId = occurrenceReportDescriptor?.group.id ?? targetGroup ?? ((existingGroups && existingGroups[0]) ? existingGroups[0].id! : 0);
            handleGroupChange({ target: { value: defaultGroupId } });
        }
    }, [])

    React.useEffect(() => {
        if (occurrenceReportDescriptor) {
            loadDescriptor(occurrenceReportDescriptor, targetGroup || undefined);
            loadTerms(occurrenceReportDescriptor.descriptorTerms);
        }
    }, [occurrenceReportDescriptor, targetGroup, loadDescriptor, loadTerms])


    // Wrapped handlers that track changes
    const handleNameChangeWithTracking = React.useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        handleNameChange(event);
        setHasUnsavedChanges(true);
    }, [handleNameChange]);

    const handleDescriptionChangeWithTracking = React.useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        handleDescriptionChange(event);
        setHasUnsavedChanges(true);
    }, [handleDescriptionChange]);

    const handleUriChangeWithTracking = React.useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        handleUriChange(event);
        setHasUnsavedChanges(true);
    }, [handleUriChange]);

    const handleGroupChangeWithTracking = React.useCallback((event: any) => {
        handleGroupChange(event);
        setHasUnsavedChanges(true);
    }, [handleGroupChange]);

    const updateTermKeyWithTracking = React.useCallback((event: React.ChangeEvent<HTMLTextAreaElement>, index: number) => {
        updateTermKey(event, index);
        setHasUnsavedChanges(true);
    }, [updateTermKey]);

    const updateTermValueWithTracking = React.useCallback((event: React.ChangeEvent<HTMLTextAreaElement>, index: number) => {
        updateTermValue(event, index);
        setHasUnsavedChanges(true);
    }, [updateTermValue]);

    const removeTermWithTracking = React.useCallback((index: number) => {
        removeTerm(index);
        setHasUnsavedChanges(true);
    }, [removeTerm]);

    const handleAddNewTermWithTracking = React.useCallback(() => {
        handleAddNewTerm();
        setHasUnsavedChanges(true);
    }, [handleAddNewTerm]);

    const isNotEditable = (): boolean => {
        return isFieldDisabled(permissions);
    }

    const handleSave = (): void => {
        setError(null);
        
        // Validate form before saving
        if (!validateForm()) {
            toast.error('Please fix validation errors before saving');
            return;
        }
        
        const descriptorToSave = formOccurrenceReportDescriptor();
        
        if (occurrenceReportDescriptor.id) {
            // Update existing descriptor
            occurrenceReportDescriptorService.putDescriptor(descriptorToSave).then((descriptor: OccurrenceReportDescriptor) => {
                updateOccurrenceReportDescriptor(descriptor);
                setHasUnsavedChanges(false);
                toast.success('Occurrence Report Descriptor has been updated successfully.');
            }).catch((err: Error) => {
                setError(err.message);
            });
        }
        else {
            // Create new descriptor
            occurrenceReportDescriptorService.createDescriptor(descriptorToSave)
                .then((descriptor: OccurrenceReportDescriptor) => {
                    updateOccurrenceReportDescriptor(descriptor);
                    setHasUnsavedChanges(false);
                    toast.success('Occurrence Report Descriptor has been saved successfully.');
                })
                .catch((err: Error) => {
                    setError(err.message);
                });
        }        
    }

    const handlePublish = (): void => {
        setError(null);
        occurrenceReportDescriptorService.publishDescriptor(occurrenceReportDescriptor.id!).then((descriptor: OccurrenceReportDescriptor) => {
            if (workspaceObjects?.objectType === ResourceType.OCCURRENCE_REPORT_DESCRIPTORS) {
                setWorkspaceObjects({objectType: ResourceType.OCCURRENCE_REPORT_DESCRIPTORS});
            }            
            toast.success('Occurrence Report Descriptor has been published successfully.');
        }).catch((err: Error) => {
            setError(err.message);
        });
    }

    const updateOccurrenceReportDescriptor = (ord: OccurrenceReportDescriptor): void => {
        ord.group = getAdditionalResource(ResourceType.GROUPS).resources?.find((grp) => grp.id === ord.group.id);
        let descriptorObject = {objectType: ResourceType.OCCURRENCE_REPORT_DESCRIPTORS, object: ord};
        setCurrentWorkspaceObject(descriptorObject);
        if (workspaceObjects?.objectType === ResourceType.OCCURRENCE_REPORT_DESCRIPTORS) {
            setWorkspaceObjects({objectType: ResourceType.OCCURRENCE_REPORT_DESCRIPTORS});
        }
        updateTab(descriptorObject);
        
        // Publish update event for other components
        publishResourceUpdate({
            type: ResourceType.OCCURRENCE_REPORT_DESCRIPTORS,
            id: ord.id!,
            updatedData: ord,
            source: 'save'
        });
    }

    const formOccurrenceReportDescriptor = (): OccurrenceReportDescriptor => {
        const descriptorData = {} as OccurrenceReportDescriptor;
        descriptorData.name = formState.name || '';
        descriptorData.description = formState.description || '';
        descriptorData.uri = formState.uri || '';
        
        descriptorData.id = occurrenceReportDescriptor.id;
        descriptorData.group = getAdditionalResource(ResourceType.GROUPS)!.resources!.find((group) => group.id === formState.groupId);
        descriptorData.descriptorTerms = getTermsForSubmission();
        return descriptorData;
    }


    if (groups && groups[0] && (formState.groupId == 0 || isNaN(formState.groupId))) { 
        handleGroupChange({ target: { value: groups[0].id! } });
    }

    return (
        <Box sx={{ height: '100%', p: 1 }}>
            <Box sx={{ width: '100%' }}>
                <Stack spacing={2} direction="column" justifyContent='space-evenly'>
                    <DescriptorBasicInfo
                        formState={formState}
                        validation={validation}
                        groups={groups}
                        descriptor={occurrenceReportDescriptor}
                        componentId={uuidRef.current}
                        isNotEditable={isNotEditable()}
                        onNameChange={handleNameChangeWithTracking}
                        onDescriptionChange={handleDescriptionChangeWithTracking}
                        onUriChange={handleUriChangeWithTracking}
                        onUriBlur={handleUriBlur}
                        onGroupChange={handleGroupChangeWithTracking}
                    />

                    <DescriptorTermsTable
                        termEntries={termEntries}
                        componentId={uuidRef.current}
                        isNotEditable={isNotEditable()}
                        onUpdateTermKey={updateTermKeyWithTracking}
                        onUpdateTermValue={updateTermValueWithTracking}
                        onRemoveTerm={removeTermWithTracking}
                        onAddNewTerm={handleAddNewTermWithTracking}
                        canAddNewTerm={canAddNewTerm}
                        canRemoveTerm={canRemoveTerm}
                    />

                    {(!occurrenceReportDescriptor.descriptorState || occurrenceReportDescriptor.descriptorState === DescriptorState.DRAFT) && (
                        <DescriptorActions
                            permissions={permissions}
                            onSave={handleSave}
                            onPublish={handlePublish}
                        />
                    )}

                    {occurrenceReportDescriptor.id && (!occurrenceReportDescriptor.descriptorState || occurrenceReportDescriptor.descriptorState === DescriptorState.DRAFT) && (
                        <DescriptorTest
                            descriptorId={occurrenceReportDescriptor.id}
                            isDisabled={isNotEditable()}
                            hasUnsavedChanges={hasUnsavedChanges}
                        />
                    )}

                    {occurrenceReportDescriptor.id && occurrenceReportDescriptor.descriptorState !== DescriptorState.DRAFT && (
                        <DescriptorApply
                            descriptorId={occurrenceReportDescriptor.id}
                            descriptorName={occurrenceReportDescriptor.name}
                        />
                    )}

                    {error && <ErrorAlertComponent message={error} />}
                </Stack>
            </Box>
        </Box>
    );
}